/**
 * @file        node-modules.js         Node-specific support for sending jobs to the scheduler
 *                                      which use modules supplied by the local filesystem.
 * @author      Wes Garland, wes@kingsds.network
 * @date        May 2020
 */
'use strict';
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const utils = require('dcp/utils');
const process = requireNative('process');
const path = requireNative('path');
const os = requireNative('os');
const fs = requireNative('fs');
const pathPrivacyMode = true; /** @todo XXX tie into job, false for localExec(?) */
let debugging = require('dcp/debugging').scope('job-modules');

// @ts-ignore
if (!debugging() && exports.debug) {
  debugging = function nodeModules$$debugging() { return true };
}

/** 
 * Fix windows backslashes, which are a problem when re-interpreted by functions like fs.open() 
 * (because they are interpreted as escapes).  This "fix" changes them from backslashes to forward
 * slashes, which node (and hench webpack) errorneously interpret as directory separators on win32.
 * 
 * Doing this fix every time we get a path from the OS (eg via path or require.resolve) means we 
 * can mostly treat windows paths, unix paths, and commonjs paths the same way everywhere, with the
 * notable except of absolute paths like c:/path/to/module.js - these must be re-written when used
 * as part of a canonical module identifier (into /mnt/c/path/to/module.js), and must be detected
 * via path.isAbsolute() rather than looking at the first character of the path.
 */
function fixSlashes(diskPath) {
  diskPath = os.platform() === 'win32' ? diskPath.replace(/[\\]/g, '/') : diskPath;
  return diskPath;
}

/** Create a module bundle in the form of a BravoJS module which, when evaluated, injects a module bunch of
 *  modules into the running BravoJS environment.  The module bundles are currently created for NodeJS via
 *  webpack, but that is an implementation detail.
 *
 *  @param      ${requireList}  {string | Array}        module identifier or list of module identifiers
 *  @param      buildMode       {string | undefined}    'development' for source maps or 'production' for minified
 *  @param      outerRequrire   {string | undefined}    require function to use to resolve module identifiers.
 *                                                      If undefined, resolve relative to program modulue.
 *
 *  @todo  This needs to be changed to use async calls for the spawned process (maintaining stdio mappings!)
 *         and the file copying operations, so that we can deploy the job at the same time as the I/O heavy
 *         stuff is happening on the local box.  /wg may 2020
 */
exports.createModuleBundle = async function job$$createModuleBundle(requireList, buildMode, outerRequire) {
  const webpack = requireNative('webpack');
  const child_process = requireNative('child_process');
  
  var configuration;
  var webpackOutput, webpackInput, webpackConfig, finalOutput;
  var unresolved = [];      /* list of module identifiers which could not be resolved (hopefully are deployed on network) */
  var bundlePathMap = {};   /* table mapping canonical id to real path. Usually similar, not always (thanks npm) */
  var resolveMap = {};      /* table mapping module identifier to canonical id */
  var searchPaths = {};     /* table (that becomes a list) enumerating searchable directories discovered during resolution */
  var mainModuleDir, modulePaths;
  var child;
  var args = [];
  var prologue, epilogue, md5Stream;

  if (!buildMode)
    buildMode = 'development';

  if (!outerRequire)
  {
    const moduleModule = requireNative('module');
    const createRequire = moduleModule.createRequire || moduleModule.createRequireFromPath;
    outerRequire = createRequire(process.argv[1]);
  }

  if (outerRequire.main)
  {
    mainModuleDir = canonicalize(fixSlashes(path.dirname(outerRequire.main.filename)), outerRequire, null, []);
    modulePaths = outerRequire.main.paths;
  }
  else
  {
    // When there is no main module use current directory and don't allow any extra paths
    mainModuleDir = canonicalize(fixSlashes(process.cwd()), outerRequire, null, []);
    modulePaths = [];
  }

  for (let moduleIdentifier of requireList) {
    let canonicalPath = canonicalize(moduleIdentifier, outerRequire, mainModuleDir, modulePaths);
    if (!canonicalPath) {
      unresolved.push(moduleIdentifier);
      continue;
    }
    bundlePathMap[canonicalPath] = moduleIdentifier;
    resolveMap[moduleIdentifier] = canonicalPath;
    if (canonicalPath.startsWith('/localhost/program/node_modules/') || canonicalPath.startsWith('/localhost/search/node_modules/')) {
      let npmDir = canonicalPath.split('/').slice(0,5).join('/');
      searchPaths[npmDir] = true;
    } else if (canonicalPath.startsWith('/localhost/search/')) {
      let npmDir = canonicalPath.split('/').slice(0,4).join('/');
      searchPaths[npmDir] = true;
    }
  }
  if (Object.keys(bundlePathMap).length === 0) {
    return { unresolved };
  }
  
  /* Create the module bundle, exporting the modules that will be packed into it 
   * and annotating the effects of search path expansion package.json magic.
   */
  try {
    webpackOutput = utils.createTempFile(`dcp-${process.pid}-module-bundle-XXXXXXXX`, 'js').closeSync();
    webpackInput  = utils.createTempFile(`dcp-${process.pid}-module-index-XXXXXXXX`,  'js');
    webpackConfig = utils.createTempFile(`dcp-${process.pid}-module-config-XXXXXXXX`, 'json');
    finalOutput   = utils.createTempFile(`dcp-${process.pid}-final-bundle-XXXXXXXX`,  'js');

    searchPaths = Object.keys(searchPaths);
    webpackInput.writeSync('Object.assign(exports, {\n');
    for (let moduleId in bundlePathMap) {
      let diskPath = fixSlashes(outerRequire.resolve(bundlePathMap[moduleId]));
      webpackInput.writeSync(`  "${moduleId}": require("${diskPath}"),\n`);
    }
    
    webpackInput.writeSync('});\n');
    webpackInput.writeSync(`defineSearchPaths(${JSON.stringify(searchPaths)});\n`);
    webpackInput.writeSync(`defineResolveMap(${JSON.stringify(resolveMap)});\n`);

    debugging('webpack') && console.log(`webpackInput(${webpackInput.filename}):`, fs.readFileSync(webpackInput.filename, 'utf8'));

    /* Run webpack in a separate process to avoid having to webpack the webpack source code.
     * That fattens up the client-bundle and causes many problems like using a browser version
     * of Buffer when running in node. The output of the webpack is the module bundle,
     * in amd define() transport format.
     */
    configuration = {
      mode:  buildMode,
      target: [ 'es6' ],
      optimization: {
        minimize: buildMode === 'production',
      },
      entry: webpackInput.filename,
      output: {
        path:     path.dirname(webpackOutput.filename),
        filename: path.basename(webpackOutput.filename),
        pathinfo: buildMode !== 'production',
        chunkFormat: 'commonjs',
        library: {
          type: 'amd',
          name: 'dcp-localhost',
        }
      },
      resolve: {
        fallback: {
          fs: false,
          crypto: false,
        },
      },
      watch: false, // Do not watch for changes in resolved files.
    }

    if (typeof dcpConfig.job.modules.webpackOptions === 'object')
      configuration = utils.leafMerge(configuration, dcpConfig.job.modules.webpackOptions);
    if (typeof process.env.DCP_NODE_MODULES_WEBPACK_OPTIONS === 'object')
      configuration = utils.leafMerge(configuration, process.env.DCP_NODE_MODULES_WEBPACK_OPTIONS);

    webpackConfig.writeSync(JSON.stringify(configuration));
    debugging('webpack') && console.debug('webpackConfig:', JSON.stringify(configuration, null, 2));

    if (parseInt(webpack.version, 10) !== 5)
      throw new Error('Webpack ' + webpack.version + ' is not supported');

    const inMemWebpack = fixSlashes(path.resolve(path.dirname(requireNative.resolve('.')), 'bin', 'dcp-module-bundler'));
    let success = fs.existsSync(inMemWebpack); // back-compat
    if (success)
    {
      /* Spawn webpack using an in-memory fileSystem. */
      args[0] = process.execPath;
      args[1] = inMemWebpack;
      args[2] = webpackConfig.filename;

      debugging() && console.debug('Creating job.requires(dependencies) bundle using in-memory fileSystem:', args);
      child = child_process.spawnSync(args[0], args.slice(1), {
        shell: false,
        windowsHide: true,
        stdio: 'inherit',
      });

      if (child.status !== 0)
      {
        debugging() && console.warn(`Webpack compilation failure with exit code ${child.status} and stderr ${child.stderr}`);
        success = false;
        /** @todo Remove this branch so it unconditionaly throws (and kill success=false), after December 14, 2025. */
        if (!dcpConfig.job.modules.webpackFallbackToOldPattern)
          throw new Error(`Webpack in-memory fileSystem: child process returned exit code ${child.status} with stderr ${child.stderr}`);
      }
    }

    /** Back-compat: Older client bundles need to go through this webpack technique.
     *  The downside is webpack output is not reproducible because chunkIds and moduleIds are
     *  seeded with the input file name, webpackInput.filename, which is random.
     *  >>>Remove the below comment on December 14, 2025<<<
     *  Temporarily when in-memory webpack input technique fails, revert to this webpack technique.
     */
    if (!success)
    {
      args[0] = process.execPath;
      args[1] = fixSlashes(path.resolve(path.dirname(requireNative.resolve('webpack')), '..', 'bin', 'webpack.js'));
      args[2] = `--config=${webpackConfig.filename}`;

      debugging() && console.debug('Creating job.requires(dependencies) bundle:', args);
      child = child_process.spawnSync(args[0], args.slice(1), {
        shell: false,
        windowsHide: true,
        stdio: 'inherit',
      });

      if (child.status !== 0)
        throw new Error(`Webpack: child process returned exit code ${child.status} with stderr ${child.stderr}`);
    }

    debugging('webpack') && console.log("Webpack output:", fs.readFileSync(path.join(configuration.output.path, configuration.output.filename), 'utf8'));

    /* Add a wrapper function around the webpack output which turns it into a valid
     * CommonJS Modules/2.0-draft8 (eg BravoJS) module that does not pollute scope.
     * Calculate the while we're at it to avoid double-reading large files.
     */
    ({prologue, epilogue} = sideloadGenerator());
    md5Stream = require('create-hash')('md5');
    finalOutput.writeSync(prologue);
    md5Stream.write(prologue);
    utils.catFile(webpackOutput, finalOutput, md5Stream);
    finalOutput.writeSync(epilogue);
    md5Stream.write(epilogue);
    md5Stream.end();
  } finally {
    webpackInput.closeSync();
    webpackConfig.closeSync();
    finalOutput.closeSync();

    webpackInput.remove();
    webpackConfig.remove();
    webpackOutput.remove();
  }

  return {
    tempFile: finalOutput,
    hash: md5Stream.read(),
    unresolved: unresolved,
  }
}

/** 
 * Inject the modules in a module bundle into the current BravoJS environment's
 * internal module memo, after invoking module.declare() so that we can masquerade 
 * as a simple module dependency.
 *
 * @note This function exists in source form so that it isn't annoying to edit. 
 *       It gets used by being converted to string and parsed by sideloadGenerator().
 *       The line containing the define("webpack...") string must not be changed, 
 *       as it is a parse token.
 *
 *       Additionally, all newlines will be eliminated to preserve the webpack source
 *       map, so no C++-style comments should be used, and semicolons are mandatory.
 *       Whitespace is also compacted, even inside string literals.
 */
const sideloader = `function sideloader() {
  module.declare(function(){});
  (function IIFE() { /* inject webpacked module group into BravoJS' module memo */
    /* Add program module paths discovered during bundling to global require path */
    function defineSearchPaths(searchPaths) {
      for (path of searchPaths)
        bravojs.paths.push(path);
    }
    /* Add name mapping for requires args to cover cases where resolution happens in package.json */
    function defineResolveMap(pathMap) {
      if (!bravojs.resolveMap)
        bravojs.resolveMap = {};
      bravojs.resolveMap = Object.assign(bravojs.resolveMap, pathMap);
      if (!bravojs.globalResolveHook) {
        bravojs.globalResolveHook = function dcp$$define$resolve(moduleIdentifier) {
          if (bravojs.resolveMap.hasOwnProperty(moduleIdentifier))
            return bravojs.resolveMap[moduleIdentifier];
          else
            return moduleIdentifier;
        }
      }
    }
    function define(amdModuleLabel, amdModuleDeps, amdModuleFactory) {
      var moduleGroup = amdModuleFactory();

      for (let moduleIdentifier in moduleGroup) {
        if (!moduleGroup.hasOwnProperty(moduleIdentifier))
          continue;
        bravojs.requireMemo[moduleIdentifier] = moduleGroup[moduleIdentifier];
        if (bravojs.debug)
          console.log(\`Memoized module \${moduleIdentifier}: \${Object.keys(moduleGroup[moduleIdentifier])}\`);
      }
      if (bravojs.mainModule !== '/localhost/program') {
        bravojs.mainModuleDir = '/localhost/program';
        bravojs.global.require = bravojs.requireFactory(bravojs.mainModuleDir);
      }
    }
    define("webpack amd output here", [], factoryFunction);
  })();
}`;

/** Generate the prologue and epilogue for a webpack in amd library mode which, when 
 *  all three parts are joined, will sideload the webpack's exports into BravoJS'
 *  running require memo, exposing them as require() modules.
 */
function sideloadGenerator() {
  var slInner = sideloader.toString().replace(/^[^{]*{/,'').replace(/} *$/,'');
  var offset_define = slInner.indexOf('define("webpack amd output here"');
  var offset_epilogue = slInner.slice(offset_define).indexOf('\n');
  var prologue, epilogue;
  
  if (offset_define === -1 || offset_epilogue === -1) {
    throw new Error(`sideload function missing parse token(s)`);
  }

  prologue = slInner.slice(0, offset_define)               .replace(/[ \n\r\t]+/g, ' ').replace(/(^ | $)/g,'');
  epilogue = slInner.slice(offset_define + offset_epilogue).replace(/[ \n\r\t]+/g, ' ').replace(/(^ | $)/g,'');

  return {prologue, epilogue};
}

/** Return the canonical module id for a given module. All of the modules begin
 *  begin in the localhost directory off the conceptual module system root, as these
 *  are "local" -- not deployed -- modules.  Otherwise, they mostly represent the
 *  structure on disk, although this is explicitly a path within the conceptual module
 *  system space and not actually any physical filesystem.
 * 
 *  In "release" mode, we further modify the canonical path to preserve privacy
 *  wrt the developer's disk layout: All search-path directories are "collapsed" 
 *  onto a single directory, and the program-module directory is anonymized. In
 *  that case, the program module's "directory" is /localhost/program/ and the
 *  search path is /localhost/search/.
 *
 *  @param {string}     moduleIdentifier        a module identifier (argument to require()) or absolute path to module
 *  @param {function}   outerRequire            the require() function used by the node code (not the work function)
 *  @param {string}     mainModuleDir           the directory of the main (program) module; used to calculate safety
 *                                              of relative paths when in path-privacy mode.  Passing excatly null
 *                                              disables this path-privacy mode.
 *  @param {Array}      modulePaths             List of paths to search (like require.paths in CJS)
 */
function canonicalize(moduleIdentifier, outerRequire, mainModuleDir, modulePaths) {
  var filename, fno;
  var canonical = '';
  var doPathPrivacy = pathPrivacyMode && mainModuleDir !== null;
  
  try {
    filename = (path.isAbsolute(moduleIdentifier) ? path : outerRequire).resolve(moduleIdentifier);
    fixSlashes(filename);
  } catch(e) {
    if (moduleIdentifier.startsWith('./') ||
        moduleIdentifier.startsWith('../') ||
        moduleIdentifier.startsWith('/') ||
        path.isAbsolute(moduleIdentifier)) {
      throw new Error(`Cannot locate module '${moduleIdentifier}'`);
    }
    debugging() && console.warn(`Cannot locate module ${moduleIdentifier}; assuming on deployed search path`);
    return null;
  }

  fno = path.parse(filename);
  if (os.platform() === 'win32' && fno.root.match(/[A-Za-z]:/)) {
   /* put drive letters into a virtual mount point */
    let newDir = '/mnt/' + fno.root[0].toLowerCase();
    if (fno.dir != fno.root)
      newDir += '/' + fno.dir.slice(fno.root.length);
    fno.root = '/';
    fno.dir = newDir;
  }
  /* root is always /
   * dir always starts with a / unless its empty and does not end in a /
   * base never starts with a / and does not end in a /
   */  
  canonical = fno.root;
  if (fno.dir)
    canonical += fno.dir.slice(fno.root.length);
  if (fno.dir && fno.dir.length !== fno.root.length) /* if the directory is root we don't need an extra '/' */
    canonical += '/'
  canonical += fno.name;
  canonical = fixSlashes(canonical);

  if (doPathPrivacy) {
    mainModuleDir = fixSlashes(mainModuleDir);
    let mmRE = new RegExp(`^${mainModuleDir}(/|$)`);
    if (canonical.match(mmRE)) {
      canonical = '/program' + canonical.slice(mainModuleDir.length);
    } else {
      if (moduleIdentifier.startsWith('./') || moduleIdentifier.startsWith('../')) {
        throw new Error(`Relative module identifiers above the program module's directory are not supported in path privacy mode (${canonical})`);
      }
      canonical = '/search' + moduleIdentifier;
    }
  }

  if (doPathPrivacy)
    return '/localhost' + canonical;
  else
    return canonical;
}
